import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { endTrial, pauseAccount } from 'services/account.service';
import {
  loadAccount,
  loadUpcomingInvoice,
  loadUsage,
} from 'store/modules/settings/actions';
import get from 'lodash/get';
import ClearIcon from '@mui/icons-material/Clear';
import * as Sentry from '@sentry/browser';
import { toMoney } from 'utils';
import cookie from 'utils/cookie';
import { EMPTY_TIMESTAMP } from 'constants/index';
import toast from 'utils/toast';
import useAlert from 'hooks/useAlert';
import DLCStatus from 'constants/10-dlc-status';

const Root = styled(Box)`
  top: 0;
  margin: 0;
  position: relative;
  clear: both;
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default () => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const [t] = useTranslation(['settings']);

  const account = useSelector((s) => s.settings.account);
  const { usage, upcomingInvoice } = account;
  const amount = upcomingInvoice ? upcomingInvoice.amount_due : 0;

  useEffect(() => {
    dispatch(loadUsage(account.account_id));
  }, []);

  useEffect(() => {
    if (account.hash) {
      dispatch(loadUpcomingInvoice(account.stripe_id)).catch((e) =>
        console.log(e),
      );
    }
  }, [account.subscriptions]);

  const onStart = () => {
    alert.confirm({
      title: (
        <Box textAlign="center">
          {t('start-membership-title', 'Start My Membership')}
        </Box>
      ),
      description: (
        <Box mt={2}>
          <Box textAlign="center" mb={3}>
            <img
              src="/assets/svg/illustration-orientation.svg"
              style={{ width: 200 }}
            />
          </Box>
          <Typography>
            {t('start-membership-message', {
              amount: toMoney(amount),
              defaultValue: `Thanks for choosing Text In Church! You will be billed {{amount}} today & your full membership will be active immediately!`,
            })}
          </Typography>
        </Box>
      ),
      confirmationText: t(
        'settings:start-membership-confirm',
        'Start Membership',
      ),
      onConfirm: () => {
        return endTrial(account).then(
          () => {
            toast.success(
              t(
                'start-membership-success',
                "You just made a great decision! Don't be surprised if your attendance increases and your people feel more connected.",
              ),
            );
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          },
          (err) => {
            Sentry.captureEvent({
              message: 'Upgrade subscription error: ' + err.message,
              level: 'critical',
            });
            toast.error(err.message);
          },
        );
      },
    });
  };

  const smsCount = get(usage, 'plan.sms', 0);
  const usedCount = get(usage, 'counts.sms', 0);
  const trialEndDate = moment.utc(account.account_trial_end_date);
  const usedPc = Math.round((usedCount / smsCount) * 100);
  const overUsedCount = usedCount - smsCount;
  const isTrialing =
    account.account_is_paused === '0' &&
    account.account_trial_end_date &&
    account.account_trial_end_date !== EMPTY_TIMESTAMP &&
    trialEndDate.isAfter(moment.utc());

  if (isTrialing) {
    return (
      <Root
        sx={{
          bgcolor: '#038AFF0D',
          color: 'primary.main',
          display: 'inline-flex',
          p: 1,
          mx: 'auto',
          borderRadius: 1,
        }}
      >
        {usedPc >= 100 ? (
          <Typography color="inherit" fontWeight={600}>
            {t('trial-used-all', 'You have used all of your trial messages.')}
          </Typography>
        ) : (
          <Typography fontWeight={600} color="inherit">
            Enjoy your <b>free trial</b>! You have{' '}
            {trialEndDate.diff(moment.utc(), 'd')} days remaining on your free
            trial.
          </Typography>
        )}
        {account.account_10dlc_status === DLCStatus.COMPLETED && (
          <Button sx={{ ml: 1 }} onClick={onStart}>
            {t('start-membership-button', 'Start My Membership')}
          </Button>
        )}
      </Root>
    );
  } else if (overUsedCount > 0) {
    return (
      <Root
        sx={{
          bgcolor: '#8235F40D',
          color: '#8235F4',
          display: 'inline-flex',
          p: 1,
          mx: 'auto',
          borderRadius: 1,
        }}
      >
        <Typography color="inherit">
          <strong>Overage Alert!</strong> You’ve sent {usedCount} of your{' '}
          {smsCount} message allotment. Overages will be billed at 3 cents per
          message.
        </Typography>
        <Button
          sx={{
            'ml': 1,
            'bgcolor': '#8235F4',
            '&:hover': {
              bgcolor: '#8235F4',
            },
          }}
          component={Link}
          to="/settings/subscription"
        >
          Add Messages
        </Button>
      </Root>
    );
  } else if (usedPc >= 80) {
    return (
      <Root
        sx={{
          bgcolor: '#038AFF0D',
          color: 'primary.main',
          display: 'inline-flex',
          p: 1,
          mx: 'auto',
          borderRadius: 1,
        }}
      >
        <Typography color="inherit">
          Your <b>message threshold</b> is approaching. You’ve sent {usedCount}{' '}
          of your {smsCount} message allotment.
        </Typography>
        <Button
          sx={{
            ml: 1,
          }}
          component={Link}
          to="/settings/subscription"
        >
          Add Messages
        </Button>
      </Root>
    );
  }
  return null;
};
